import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, useMediaQuery, useTheme } from '@mui/material'

import {
  ProductsGrid,
  LayoutFormat,
  LayoutButtons,
  Filter,
  ProductTable,
  PaginationButtons,
  PaginationContext,
} from 'src/components'
import {
  initialProductsAllFilter,
  useAccountsSelectionOptions,
  useAIGeneratedEntitySelectionOptions,
  useAllProducts,
  useFilter,
  useJourneysAllRawSelectionOption,
  usePagination,
  useStepsAllRawSelectionOption,
} from 'src/hooks'

import {
  defaultProductsAllLimit,
  defaultSort,
  defaultPage,
  productsSortOptions,
} from './filter/default.filter'
import { countOffset, sortToObject } from 'src/common/utils'

const ProductsAll = () => {
  const [t] = useTranslation()
  const [filter, setFilter] = useFilter({ initialState: initialProductsAllFilter })
  const [sort, setSort] = useState(defaultSort)
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(
    defaultPage,
    defaultProductsAllLimit,
  )
  const theme = useTheme()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop_l'))
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const [layoutFormat, setLayoutFormat] = useState<LayoutFormat>(
    isLaptop ? LayoutFormat.Grid : LayoutFormat.Table,
  )
  const { data: products, refetch: refetchProducts } = useAllProducts({
    ...filter,
    offset: countOffset(page, rowsPerPage),
    limit: rowsPerPage,
    ...sortToObject(sort),
  })

  const accountsSelectionOption = useAccountsSelectionOptions()
  const stepsAllRawSelectionOption = useStepsAllRawSelectionOption()
  const journeysAllRawSelectionOption = useJourneysAllRawSelectionOption()
  const aiGeneratedEntitySelectionOptions = useAIGeneratedEntitySelectionOptions()

  useEffect(() => {
    refetchProducts()
  }, [filter, sort, page, rowsPerPage, refetchProducts])

  useEffect(() => {
    setPage(defaultPage)
  }, [filter, setPage, sort])

  const handleLayoutChange = () => setLayoutFormat(layoutFormat > 0 ? 0 : 1)

  const paginationValues = {
    count: products?.count ?? 0,
    page: page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!products && products.count > rowsPerPage,
  }

  const selectionOptions = [
    { ...accountsSelectionOption, withSearch: true },
    { ...stepsAllRawSelectionOption, withSearch: true },
    { ...journeysAllRawSelectionOption, withSearch: true },
    aiGeneratedEntitySelectionOptions,
  ]

  return (
    <Grid container>
      <Filter
        sort={sort}
        setSort={setSort}
        searchFieldText={t('products-all-search-input.v')}
        filter={filter}
        setFilter={setFilter}
        selectionOptions={selectionOptions}
        sortOptions={productsSortOptions}
        initialState={initialProductsAllFilter}
      />
      <PaginationContext.Provider value={paginationValues}>
        <LayoutButtons layoutFormat={layoutFormat} handleLayoutChange={handleLayoutChange} />
        {layoutFormat === LayoutFormat.Table ? (
          <ProductTable
            withoutAddition
            withAccount
            sort={sort}
            setSort={setSort}
            products={products?.data}
          />
        ) : (
          <ProductsGrid withoutAddition products={products?.data} />
        )}
        {!isTablet && <PaginationButtons />}
      </PaginationContext.Provider>
    </Grid>
  )
}

export { ProductsAll }
